import { COMPONENT_GROUP_LABELS, ComponentDef, InlineDef, RegistryLookup } from '../types';
import { AnswerCode } from './blocks/AnswerCode';
import { ArticleLinkCard } from './blocks/ArticleLinkCard';
import { ArticleNav } from './blocks/ArticleNav';
import { Callout } from './blocks/Callout';
import { Card } from './blocks/Card';
import { Checklist } from './blocks/Checklist';
import { CodeBlock } from './blocks/CodeBlock';
import { Expected } from './blocks/Expected';
import { Grid } from './blocks/Grid';
import { LinkCard } from './blocks/LinkCard';
import { List } from './blocks/List';
import { Mermaid } from './blocks/Mermaid';
import { Quote } from './blocks/Quote';
import { Section } from './blocks/Section';
import { Tab } from './blocks/Tab';
import { Table } from './blocks/Table';
import { Tabs } from './blocks/Tabs';
import { Text } from './blocks/Text';
import { Tip } from './blocks/Tip';
import { Toc } from './blocks/Toc';
import { Chapter } from './blocks/prose/Chapter';
import { ProseCallout } from './blocks/prose/ProseCallout';
import { ProseEpigraph } from './blocks/prose/ProseEpigraph';
import { ProseHeading } from './blocks/prose/ProseHeading';
import { ProseList } from './blocks/prose/ProseList';
import { ProseQuote } from './blocks/prose/ProseQuote';
import { ProseSource } from './blocks/prose/ProseSource';
import { ProseTable } from './blocks/prose/ProseTable';
import { ProseText } from './blocks/prose/ProseText';
import { BadgeInline } from './inline/BadgeInline';
import { KbdInline } from './inline/KbdInline';
import { UserMentionInline } from './inline/UserMentionInline';

/**
 * knz コンポーネント定義（single source of truth）。
 * パース検証・ビューワー描画・エディタ補完・リファレンス画面がこの定義を共有する。
 */
export const COMPONENT_DEFS: ComponentDef[] = [
  {
    name: 'AnswerCode',
    group: 'data',
    label: '正解コード（研修設問・非表示）',
    description:
      '研修設問の正解コード（言語ごと）を定義するメタデータ。file に正解コードのファイルパスを指定し、expected に紐づく Expected の id を `- id` で列挙する。記事本文としては描画されない（設問のプレフィル用）。バックエンドはknzをパースせず、反映時にフロントがパス→fileIdを解決して構造化データを送る。',
    acceptsChildren: false,
    component: AnswerCode,
    params: [
      {
        key: 'file',
        type: 'string',
        required: true,
        description: '正解コードのファイルパス（例 /src/Main.java）。拡張子から言語が解決される',
      },
      {
        key: 'expected',
        type: 'idRef[]',
        required: true,
        description: '紐づく Expected（期待値）ブロックの id を `- id` で列挙する',
      },
    ],
    template: [
      '<--- AnswerCode --->',
      'file:/src/Main.java',
      'expected:',
      ' - e-1',
      '<--- !AnswerCode --->',
    ].join('\n'),
    notes: [
      'このブロックは記事本文には描画されません（設問登録のプレフィル用メタデータ）。',
      'file は workspace の実行可能拡張子ファイルのパスを指定します（例 /src/Main.java）。',
      '同一設問内で同一言語（拡張子）の正解コードは1つまでです。',
      '期待値は children ではなく expected（id リスト）で紐づけます。Expected ブロックはファイル内のどこに置いても構いません。',
    ],
    recipes: [
      {
        title: '正解コードに期待値を紐づける',
        description:
          'AnswerCode の expected に Expected の id を `- id` で列挙し、Expected ブロックを別途定義します（Expected は本文に描画されません）。',
        source: [
          '<--- AnswerCode --->',
          'file:/src/Main.java',
          'expected:',
          ' - e-1',
          ' - e-2',
          '<--- !AnswerCode --->',
          '<--- Expected --->',
          'id:e-1',
          'sourceFile:/src/Main.java',
          'sourceSeq:1',
          '<--- !Expected --->',
          '<--- Expected --->',
          'id:e-2',
          'sourceFile:/src/Main.java',
          'sourceSeq:2',
          '<--- !Expected --->',
        ].join('\n'),
      },
    ],
  },
  {
    name: 'Expected',
    group: 'data',
    label: '期待値（研修設問・非表示）',
    description:
      '研修設問の期待値を定義するメタデータ。id は AnswerCode の expected から参照される。sourceFile / sourceSeq に由来の実行履歴を指定する。記事本文としては描画されない（設問のプレフィル用）。実行時引数・標準出力はknzには書かず、反映時に実行履歴からDBへコピーされる（D6-B3）。sourceFile はファイルパスで書く。',
    acceptsChildren: false,
    component: Expected,
    params: [
      {
        key: 'id',
        type: 'string',
        required: true,
        description: 'AnswerCode の expected から参照するための識別子',
      },
      {
        key: 'sourceFile',
        type: 'string',
        required: true,
        description: '由来の実行履歴のファイルパス（例 /src/Main.java）',
      },
      {
        key: 'sourceSeq',
        type: 'number',
        required: true,
        description: '由来の実行履歴の連番（seq）',
      },
    ],
    template: [
      '<--- Expected --->',
      'id:e-1',
      'sourceFile:/src/Main.java',
      'sourceSeq:1',
      '<--- !Expected --->',
    ].join('\n'),
    notes: [
      'このブロックは記事本文には描画されません（設問登録のプレフィル用メタデータ）。',
      'id を AnswerCode の expected から `- id` で参照します。',
      'sourceFile の実行履歴を sourceSeq で指定します（sourceFile はパスで書きます）。',
      '実行時引数・標準出力は knz には書きません（由来から自動でコピー保持されます）。',
    ],
  },
  {
    name: 'Text',
    group: 'text',
    label: '本文テキスト',
    description:
      '通常の段落テキスト。Markdown インラインとインライントークンを含められる。variant で文字サイズ・書体、color で文字色を変えられる。',
    acceptsChildren: false,
    textAs: 'joined',
    component: Text,
    params: [
      { key: 'text', type: 'string', required: true, description: '本文（Markdown 可）' },
      {
        key: 'variant',
        type: 'string',
        required: false,
        description: 'MUI Typography の variant（文字サイズ・書体）',
        sampleValues: ['h4', 'h5', 'h6', 'subtitle1', 'subtitle2', 'body1', 'body2', 'caption'],
        defaultValue: 'body2',
      },
      {
        key: 'color',
        type: 'string',
        required: false,
        description: '文字色（テーマトークン or カラーコード）',
        sampleValues: [
          'text.primary',
          'text.secondary',
          'primary.main',
          'error.main',
          'success.main',
        ],
        defaultValue: 'text.secondary',
      },
      {
        key: 'indent',
        type: 'boolean',
        required: false,
        description: '段落の先頭を字下げ（デフォルト true。false で字下げなし）',
        sampleValues: ['true', 'false'],
        defaultValue: 'true',
      },
    ],
    template: ['<--- Text --->', 'text:', ' - 本文をここに書く', '<--- !Text --->'].join('\n'),
    recipes: [
      {
        title: 'variant と color を変える',
        description: 'variant で見出し風に大きくしたり、color で強調色にできます。',
        source: [
          '<--- Text --->',
          'variant:h5',
          'color:primary.main',
          'text:',
          ' - 大きめの見出し風テキスト',
          '<--- !Text --->',
          '<--- Text --->',
          'color:error.main',
          'text:',
          ' - 赤色の注意テキスト',
          '<--- !Text --->',
        ].join('\n'),
      },
    ],
  },
  {
    name: 'Section',
    group: 'structure',
    label: 'セクション見出し',
    description:
      '本文中の章・節の見出し。番号ウォーターマークやサブ見出し帯に対応。children で配下ブロックを持つ。',
    acceptsChildren: true,
    component: Section,
    params: [
      { key: 'title', type: 'string', required: true, description: '見出しテキスト' },
      {
        key: 'number',
        type: 'number',
        required: false,
        description: 'セクション番号（背景ウォーターマーク）',
        sampleValues: ['1', '2', '3'],
      },
      {
        key: 'subNumber',
        type: 'string',
        required: false,
        description: 'サブ見出し番号（例 2.1）。緑基調の小さめ帯になる',
        sampleValues: ['2.1', '2.2'],
      },
      { key: 'anchor', type: 'string', required: false, description: '目次リンク用アンカー' },
      {
        key: 'children',
        type: 'idRef[]',
        required: false,
        description: '配下に描画するブロックの id',
      },
    ],
    template: [
      '<--- Section --->',
      'title:セクション名',
      'number:1',
      'anchor:section-1',
      'children:',
      '<--- !Section --->',
    ].join('\n'),
    notes: [
      'children に配下ブロックの id を指定して中身を入れます（参照された子は単独では描画されません）。',
      'anchor を指定すると Toc（目次）に載り、記事公開ページでは右側の目次から該当 Section へジャンプできます。',
    ],
    recipes: [
      {
        title: 'セクションに本文を入れる（children 参照）',
        description: 'Text ブロックに id を付け、Section の children から参照します。',
        source: [
          '<--- Section --->',
          'title:概要',
          'number:1',
          'anchor:overview',
          'children:',
          ' - p1',
          '<--- !Section --->',
          '<--- Text --->',
          'id:p1',
          'text:',
          ' - ここがセクションの本文です。',
          '<--- !Text --->',
        ].join('\n'),
      },
      {
        title: 'サブ見出し（subNumber）',
        source: [
          '<--- Section --->',
          'title:準備',
          'subNumber:1.1',
          'children:',
          ' - p2',
          '<--- !Section --->',
          '<--- Text --->',
          'id:p2',
          'text:',
          ' - サブセクションの内容。',
          '<--- !Text --->',
        ].join('\n'),
      },
    ],
  },
  {
    name: 'Callout',
    group: 'text',
    label: '注意ボックス',
    description:
      '注意・補足・重要・成功を色分けして示すボックス。text だけでなく children で他のブロックを入れられる。',
    acceptsChildren: true,
    textAs: 'joined',
    component: Callout,
    params: [
      {
        key: 'type',
        type: 'string',
        required: false,
        description: '種別',
        sampleValues: ['info', 'warning', 'important', 'success'],
        defaultValue: 'info',
      },
      {
        key: 'children',
        type: 'idRef[]',
        required: false,
        description: 'ボックス内に描画するブロックの id（text より前に書く）',
      },
      { key: 'text', type: 'string', required: false, description: '本文（Markdown 可）' },
    ],
    template: [
      '<--- Callout --->',
      'type:info',
      'text:',
      ' - 補足説明をここに書く',
      '<--- !Callout --->',
    ].join('\n'),
    notes: [
      'text は Markdown が使えます（**太字**、`code`、[リンク](url) など）。',
      'インライン要素 {{kbd:Ctrl+S}} や @userId も text 内に埋め込めます。',
      'children で他のブロック（List・CodeBlock など）をボックス内に入れられます。text と併用する場合は children を text より前に書いてください。',
    ],
    recipes: [
      {
        title: '種別ごとの見え方',
        description: 'type を変えると色とアイコンが変わります。',
        source: [
          '<--- Callout --->',
          'type:warning',
          'text:',
          ' - この操作は取り消せません。',
          '<--- !Callout --->',
          '<--- Callout --->',
          'type:success',
          'text:',
          ' - 保存が完了しました。',
          '<--- !Callout --->',
        ].join('\n'),
      },
      {
        title: '本文に装飾とインライン要素を混ぜる',
        source: [
          '<--- Callout --->',
          'type:info',
          'text:',
          ' - 保存は {{kbd:Ctrl+S}} です。詳細は **設定** を確認してください。',
          '<--- !Callout --->',
        ].join('\n'),
      },
      {
        title: 'ボックス内に他のブロックを入れる（children）',
        description: 'children で List や CodeBlock などをボックス内に描画できます。',
        source: [
          '<--- Callout --->',
          'type:warning',
          'children:',
          ' - c-list',
          '<--- !Callout --->',
          '<--- List --->',
          'id:c-list',
          'variant:unordered',
          'text:',
          ' - 注意点その1',
          ' - 注意点その2',
          '<--- !List --->',
        ].join('\n'),
      },
    ],
  },
  {
    name: 'CodeBlock',
    group: 'media',
    label: 'コードブロック',
    description: 'シンタックスハイライト付きのコード表示。text は raw（無加工）で保持する。',
    acceptsChildren: false,
    textAs: 'raw',
    component: CodeBlock,
    params: [
      {
        key: 'language',
        type: 'string',
        required: false,
        description: '言語',
        sampleValues: ['ts', 'tsx', 'js', 'java', 'python', 'json', 'bash'],
      },
      { key: 'title', type: 'string', required: false, description: 'ファイル名などの見出し' },
      {
        key: 'link',
        type: 'string',
        required: false,
        description:
          'マイワークスペースのファイル（fileId）を参照。内容を動的に表示し、拡張子から言語を自動判定する。`link:` の後で補完からファイルを選べる（挿入されるのは fileId）',
      },
      {
        key: 'text',
        type: 'string',
        required: false,
        description: 'コード本体（raw。text を最後の param にする）。link 参照時は不要',
      },
    ],
    template: [
      '<--- CodeBlock --->',
      'language:ts',
      'text:',
      "const hello = 'world';",
      '<--- !CodeBlock --->',
    ].join('\n'),
    notes: [
      'text は raw（無加工）です。`-` 接頭辞は不要で、コードをそのまま貼り付けられます。',
      'raw のため text は必ず最後の param にしてください（language / title は text より前）。',
      'language を指定すると Prism でシンタックスハイライトされます。',
      'link にマイワークスペースの fileId を指定すると、その fileId のファイル名を title に、内容を text に、拡張子を language に自動設定して動的に表示します（参照先ファイルを編集すれば内容も追従）。',
      'param で title / text / language を明示した場合は、その値が取得情報より優先されます。',
      'link 参照先のファイルが取得できない場合はエラーにはならず、「参照先のファイルが存在しません」と枠内に表示します。',
    ],
  },
  {
    name: 'Quote',
    group: 'text',
    label: '引用',
    description: '出典表記付きの引用ブロック。',
    acceptsChildren: false,
    textAs: 'joined',
    component: Quote,
    params: [
      { key: 'text', type: 'string', required: true, description: '引用文' },
      { key: 'cite', type: 'string', required: false, description: '出典' },
    ],
    template: ['<--- Quote --->', 'text:', ' - 引用文', 'cite:出典', '<--- !Quote --->'].join('\n'),
  },
  {
    name: 'List',
    group: 'text',
    label: 'リスト',
    description: '箇条書き / 番号付きリスト。',
    acceptsChildren: false,
    textAs: 'array',
    component: List,
    params: [
      {
        key: 'variant',
        type: 'string',
        required: false,
        description: '種別',
        sampleValues: ['unordered', 'ordered'],
        defaultValue: 'unordered',
      },
      { key: 'text', type: 'string[]', required: true, description: '各項目（`- 項目` で列挙）' },
    ],
    template: [
      '<--- List --->',
      'variant:unordered',
      'text:',
      ' - 項目1',
      ' - 項目2',
      '<--- !List --->',
    ].join('\n'),
  },
  {
    name: 'Checklist',
    group: 'data',
    label: 'チェックリスト',
    description: 'チェックボックス付きのリスト。',
    acceptsChildren: false,
    textAs: 'array',
    component: Checklist,
    params: [
      { key: 'title', type: 'string', required: false, description: 'グループ見出し' },
      { key: 'text', type: 'string[]', required: true, description: 'チェック項目（`- 項目`）' },
    ],
    template: [
      '<--- Checklist --->',
      'title:確認事項',
      'text:',
      ' - 項目1',
      ' - 項目2',
      '<--- !Checklist --->',
    ].join('\n'),
  },
  {
    name: 'Card',
    group: 'layout',
    label: 'カード',
    description: '色付きアクセントのカード。',
    acceptsChildren: false,
    textAs: 'joined',
    component: Card,
    params: [
      { key: 'title', type: 'string', required: true, description: 'カードタイトル' },
      { key: 'text', type: 'string', required: false, description: '説明文' },
      {
        key: 'color',
        type: 'string',
        required: false,
        description: 'アクセント色（テーマトークン or カラーコード）',
        sampleValues: ['primary.main', 'success.main', 'warning.main'],
      },
      { key: 'badge', type: 'string', required: false, description: 'バッジラベル' },
    ],
    template: [
      '<--- Card --->',
      'title:カードタイトル',
      'text:',
      ' - 説明文',
      'color:primary.main',
      '<--- !Card --->',
    ].join('\n'),
  },
  {
    name: 'LinkCard',
    group: 'layout',
    label: 'リンクカード',
    description: '別タブで開くリンクカード。',
    acceptsChildren: false,
    textAs: 'joined',
    component: LinkCard,
    params: [
      { key: 'title', type: 'string', required: true, description: 'タイトル' },
      { key: 'path', type: 'string', required: true, description: '遷移先 URL / パス' },
      { key: 'text', type: 'string', required: false, description: '説明文' },
      { key: 'color', type: 'string', required: false, description: 'アクセント色' },
    ],
    template: [
      '<--- LinkCard --->',
      'title:参考リンク',
      'path:https://example.com',
      'text:',
      ' - 説明',
      '<--- !LinkCard --->',
    ].join('\n'),
  },
  {
    name: 'ArticleLinkCard',
    group: 'layout',
    label: '記事リンクカード',
    description:
      'articleId で指定した記事へリンクするカード。記事のタイトル・説明・著者・公開日を getArticle で動的に取得して埋め込む（ArticleNav と同じ取得・エラー挙動）。クリックで対象記事を開く（ワークスペースのプレビューでは別タブ、記事詳細ではルーティング遷移）。任意で section（遷移先記事の Section の anchor）を指定すると、その記事の該当セクションへスクロールする。説明は text で上書きでき、未指定なら記事の description を表示する。articleId は自分が著者の記事のみ補完に出る。',
    acceptsChildren: false,
    component: ArticleLinkCard,
    params: [
      {
        key: 'articleId',
        type: 'string',
        required: true,
        description:
          'リンク先記事の articleId。`articleId:` の後で補完から選べる（候補は自分が著者の記事のみ）',
      },
      {
        key: 'section',
        type: 'string',
        required: false,
        description:
          '遷移先記事内の Section の anchor（id ではなく anchor）。指定すると開いた記事の該当セクションへスクロールする',
      },
      {
        key: 'text',
        type: 'string',
        required: false,
        description:
          'カードに表示する説明文。指定すると記事の description より優先して表示する。section で特定セクションへ飛ばすときは、そのセクションを説明する文をここに書く',
      },
      {
        key: 'color',
        type: 'string',
        required: false,
        description: 'アクセント色（テーマトークン or カラーコード）',
        sampleValues: ['primary.main', 'success.main', 'warning.main'],
        defaultValue: 'primary.main',
      },
    ],
    template: ['<--- ArticleLinkCard --->', 'articleId:a-001', '<--- !ArticleLinkCard --->'].join(
      '\n',
    ),
    notes: [
      'articleId は覚えにくいため、`articleId:` の後で補完が使えます（候補は自分が著者の記事のみ）。',
      'タイトル・説明・著者・公開日は記事から動的に取得して表示します（記事を編集すれば追従します）。',
      '説明欄は text（任意）を指定すればそれを優先し、未指定なら記事の description を表示します。',
      '取得できない articleId（存在しない / 参照不可）や articleId 未指定のときは、エラー画面にはならず枠内にメッセージを表示します（ArticleNav と同じ挙動）。',
      'section に遷移先記事の Section の anchor（id ではなく anchor）を指定すると、その記事の該当セクションへスクロールします。特定セクションへ飛ばすときは、そのセクションを説明する文を text に書くのがおすすめです。',
    ],
    recipes: [
      {
        title: '記事の特定セクションへ飛ばす（説明を自分で書く）',
        description:
          'section に遷移先記事の Section の anchor を指定し、そのセクションを説明する文を text に書きます。',
        source: [
          '<--- ArticleLinkCard --->',
          'articleId:a-001',
          'section:overview',
          'text:このセクションで概要を説明しています',
          'color:success.main',
          '<--- !ArticleLinkCard --->',
        ].join('\n'),
      },
    ],
  },
  {
    name: 'ArticleNav',
    group: 'layout',
    label: '記事ナビ（前後・関連記事）',
    description:
      '記事の上部・下部に「前の記事 / 次の記事」を、下部に「関連する記事」を表示する記事ナビ。articles に系列＋関連の articleId を順序どおりに列挙し（自分自身 self も含める）、self を起点に前後・関連を自動導出する。1 ファイルに 1 箇所だけ書き、position（top / bottom / both）で表示位置を指定する（描画は記事の上部・下部に固定される）。articleId は自分が著者の記事のみ補完に出る。',
    acceptsChildren: false,
    component: ArticleNav,
    params: [
      {
        key: 'self',
        type: 'string',
        required: true,
        description: '自身の記事の articleId（系列内での位置の起点。articles に含める）',
      },
      {
        key: 'articles',
        type: 'string[]',
        required: true,
        description:
          '系列＋関連の articleId を順序どおりに `- id` で列挙（自分自身 self も含める）。記述順が前後関係になる',
      },
      {
        key: 'position',
        type: 'string',
        required: false,
        description: '表示位置。top=上部のみ / bottom=下部のみ / both=上下両方（既定）',
        sampleValues: ['top', 'bottom', 'both'],
        defaultValue: 'both',
      },
      {
        key: 'relatedTitle',
        type: 'string',
        required: false,
        description: '「関連する記事」見出しの文言差し替え（既定「関連する記事」）',
      },
    ],
    template: [
      '<--- ArticleNav --->',
      'self:a-002',
      'position:both',
      'articles:',
      ' - a-001',
      ' - a-002',
      ' - a-003',
      ' - a-004',
      '<--- !ArticleNav --->',
    ].join('\n'),
    notes: [
      'articles に系列＋関連の articleId を 1 件以上、順序どおりに列挙します。自分自身（self）も必ず含めます。',
      '記述順が系列順（前後関係）になります。self の 1 つ前が「前の記事」、1 つ後が「次の記事」、残りが「関連する記事」です。',
      '1 ファイルに ArticleNav は 1 箇所だけ書きます。position（top / bottom / both）で上部・下部の表示を切り替えます（描画位置は本文のどこに書いても記事の上部・下部に固定されます）。',
      '関連する記事は下部（bottom / both）にのみ表示され、最大 6 件までです。',
      'articleId は覚えにくいため、self / articles の入力では補完が使えます（候補は自分が著者の記事のみ）。',
      '取得できない記事は、関連記事＝非表示 / 前後ナビ＝無効表示になります（プレビューで存在しない articleId を指定してもエラー画面にはなりません）。',
    ],
    recipes: [
      {
        title: '上部だけに前後ナビを出す',
        description:
          'position:top にすると、記事上部にのみ前の記事 / 次の記事を表示します（関連記事は出ません）。',
        source: [
          '<--- ArticleNav --->',
          'self:a-002',
          'position:top',
          'articles:',
          ' - a-001',
          ' - a-002',
          ' - a-003',
          '<--- !ArticleNav --->',
        ].join('\n'),
      },
    ],
  },
  {
    name: 'Tip',
    group: 'text',
    label: 'ヒント（開閉）',
    description: '開閉できるワンポイント補足。',
    acceptsChildren: false,
    textAs: 'joined',
    component: Tip,
    params: [
      { key: 'title', type: 'string', required: true, description: '見出し' },
      { key: 'text', type: 'string', required: true, description: '本文' },
      {
        key: 'open',
        type: 'boolean',
        required: false,
        description: '初期展開（true で開いた状態）',
        sampleValues: ['true', 'false'],
      },
    ],
    template: [
      '<--- Tip --->',
      'title:ワンポイント',
      'text:',
      ' - 補足内容',
      '<--- !Tip --->',
    ].join('\n'),
  },
  {
    name: 'Table',
    group: 'data',
    label: 'テーブル',
    description:
      '表。ヘッダーはパイプ形式（`- 名前|説明`）とリスト形式（`- 名前` `- 説明` の 1 列 1 項目）のどちらでも指定できる。各データ行は `|` 区切り。',
    acceptsChildren: false,
    textAs: 'array',
    component: Table,
    params: [
      {
        key: 'headers',
        type: 'string[]',
        required: false,
        description:
          'ヘッダー行。パイプ形式（`- 名前|説明`）か、リスト形式（`- 名前` / `- 説明` を 1 列 1 項目で並べる）のどちらでも可',
      },
      { key: 'text', type: 'string[]', required: true, description: 'データ行（各行 `|` 区切り）' },
    ],
    template: [
      '<--- Table --->',
      'headers:',
      ' - 名前|説明',
      'text:',
      ' - A|説明A',
      ' - B|説明B',
      '<--- !Table --->',
    ].join('\n'),
    notes: [
      'ヘッダーは 2 形式に対応します: パイプ形式 `- 名前|説明`、リスト形式 `- 名前` / `- 説明`（1 列 1 項目）。',
      'データ行（text）は `|` でセルを区切ります。',
      'セル内では Markdown やインライン要素も使えます。',
    ],
    recipes: [
      {
        title: 'リスト形式のヘッダー',
        description: 'ヘッダーを 1 列 1 項目で書けます（`|` を使わない書き方）。',
        source: [
          '<--- Table --->',
          'headers:',
          ' - 名前',
          ' - 説明',
          'text:',
          ' - A|説明A',
          ' - B|説明B',
          '<--- !Table --->',
        ].join('\n'),
      },
    ],
  },
  {
    name: 'Tabs',
    group: 'layout',
    label: 'タブ',
    description: 'タブ切り替え。children で Tab ブロックを参照する。',
    acceptsChildren: true,
    component: Tabs,
    params: [
      { key: 'children', type: 'idRef[]', required: true, description: 'Tab ブロックの id' },
    ],
    template: ['<--- Tabs --->', 'children:', ' - tab1', ' - tab2', '<--- !Tabs --->'].join('\n'),
    notes: [
      'children で Tab ブロックの id を参照します。各 Tab の label がタブ見出しになります。',
      'children は `- id` を 1 行ずつ書く形式を推奨します（`,` 区切りも読めますが非推奨）。',
    ],
    recipes: [
      {
        title: 'npm / yarn のタブ',
        source: [
          '<--- Tabs --->',
          'children:',
          ' - t-npm',
          ' - t-yarn',
          '<--- !Tabs --->',
          '<--- Tab --->',
          'id:t-npm',
          'label:npm',
          'text:',
          ' - `npm install` を実行します。',
          '<--- !Tab --->',
          '<--- Tab --->',
          'id:t-yarn',
          'label:yarn',
          'text:',
          ' - `yarn add` を実行します。',
          '<--- !Tab --->',
        ].join('\n'),
      },
    ],
  },
  {
    name: 'Tab',
    group: 'layout',
    label: 'タブの中身',
    description: 'Tabs の children として使う。label がタブ見出しになる。',
    acceptsChildren: true,
    textAs: 'joined',
    component: Tab,
    params: [
      { key: 'label', type: 'string', required: true, description: 'タブ見出し' },
      { key: 'text', type: 'string', required: false, description: '本文' },
      { key: 'children', type: 'idRef[]', required: false, description: '配下ブロック' },
    ],
    template: [
      '<--- Tab --->',
      'id:tab1',
      'label:タブ1',
      'text:',
      ' - 中身',
      '<--- !Tab --->',
    ].join('\n'),
  },
  {
    name: 'Mermaid',
    group: 'media',
    label: 'Mermaid 図',
    description: 'テキストからフローチャート・シーケンス図を描画する。text は raw。',
    acceptsChildren: false,
    textAs: 'raw',
    component: Mermaid,
    params: [
      {
        key: 'text',
        type: 'string',
        required: true,
        description: 'mermaid 定義（raw。text を最後の param にする）',
      },
    ],
    template: [
      '<--- Mermaid --->',
      'text:',
      'graph LR',
      '  A[開始] --> B[処理]',
      '  B --> C[終了]',
      '<--- !Mermaid --->',
    ].join('\n'),
  },
  {
    name: 'Grid',
    group: 'layout',
    label: 'グリッド',
    description: 'カード等を横並びにするレイアウト。children を並べる。',
    acceptsChildren: true,
    component: Grid,
    params: [
      {
        key: 'columns',
        type: 'number',
        required: false,
        description: '列数（デフォルト 2）',
        sampleValues: ['2', '3'],
      },
      {
        key: 'children',
        type: 'idRef[]',
        required: true,
        description: '横並びにするブロックの id',
      },
    ],
    template: [
      '<--- Grid --->',
      'columns:2',
      'children:',
      ' - card1',
      ' - card2',
      '<--- !Grid --->',
    ].join('\n'),
    notes: [
      'children で横並びにするブロックの id を参照します。カードとの相性が良いです。',
      'children は `- id` を 1 行ずつ書く形式を推奨します（`,` 区切りも読めますが非推奨）。',
    ],
    recipes: [
      {
        title: 'カードを2列で並べる',
        source: [
          '<--- Grid --->',
          'columns:2',
          'children:',
          ' - c1',
          ' - c2',
          '<--- !Grid --->',
          '<--- Card --->',
          'id:c1',
          'title:カードA',
          'text:',
          ' - 説明A',
          'color:primary.main',
          '<--- !Card --->',
          '<--- Card --->',
          'id:c2',
          'title:カードB',
          'text:',
          ' - 説明B',
          'color:success.main',
          '<--- !Card --->',
        ].join('\n'),
      },
    ],
  },
  {
    name: 'Toc',
    group: 'structure',
    label: '目次',
    description:
      'ドキュメント内の Section（anchor + title）から目次を自動生成する。記事公開ページでは本文右側にスクロール連動の目次として表示される。',
    acceptsChildren: false,
    component: Toc,
    params: [
      {
        key: 'title',
        type: 'string',
        required: false,
        description: '目次見出し',
        defaultValue: '目次',
      },
    ],
    notes: [
      'Toc ブロックはドキュメントのどこに置いても構いません（位置に関わらず目次として扱われます）。',
      '記事公開ページ（DevInsights）では、Toc を置くと本文の右側にスクロール連動する目次（現在位置をハイライト）が表示されます。本文中には目次を描画しません。',
      'ワークスペースのプレビューや研修設問のビューでは目次は表示されません（Toc ブロックは無視されます）。',
      '目次に載せるには、各 Section に anchor と title を指定してください。anchor が無い Section は目次に出ません。',
    ],
    template: ['<--- Toc --->', '<--- !Toc --->'].join('\n'),
  },

  // --- 読み物（明朝）系: devdocs/thoughts-ai のような記事を書くためのコンポーネント ---
  // 読み物コンテナ（紙面レイアウト）は frontmatter の `prose: true` で有効化する。
  // タイトル・副題・パンくずは frontmatter の title / subtitle / breadcrumb で指定する。
  // 以下は読み物本文を構成する子ブロック（章・段落・引用など）。
  {
    name: 'Chapter',
    group: 'prose',
    label: '章見出し',
    description:
      '読み物の章見出し（「第一章」などのラベル + タイトル）。children に章の中身を入れる。',
    acceptsChildren: true,
    component: Chapter,
    params: [
      {
        key: 'label',
        type: 'string',
        required: true,
        description: '章ラベル（例: 第一章 / はじめに）',
      },
      { key: 'title', type: 'string', required: false, description: '章タイトル' },
      { key: 'children', type: 'idRef[]', required: false, description: '章の中身のブロックの id' },
    ],
    template: [
      '<--- Chapter --->',
      'label:第一章',
      'title:章のタイトル',
      'children:',
      '<--- !Chapter --->',
    ].join('\n'),
  },
  {
    name: 'ProseHeading',
    group: 'prose',
    label: '節見出し（明朝）',
    description: '読み物の章内の小見出し。',
    acceptsChildren: false,
    component: ProseHeading,
    params: [{ key: 'title', type: 'string', required: true, description: '節見出し' }],
    template: ['<--- ProseHeading --->', 'title:節のタイトル', '<--- !ProseHeading --->'].join(
      '\n',
    ),
  },
  {
    name: 'ProseText',
    group: 'prose',
    label: '段落（明朝）',
    description: '明朝体・行高2.0・冒頭字下げの読み物段落。Markdown 可。',
    acceptsChildren: false,
    textAs: 'joined',
    component: ProseText,
    params: [
      { key: 'text', type: 'string', required: true, description: '本文（Markdown 可）' },
      {
        key: 'indent',
        type: 'boolean',
        required: false,
        description: '冒頭字下げ（false で字下げなし）',
        sampleValues: ['true', 'false'],
      },
    ],
    template: [
      '<--- ProseText --->',
      'text:',
      ' - 本文をここに書く。',
      '<--- !ProseText --->',
    ].join('\n'),
  },
  {
    name: 'ProseQuote',
    group: 'prose',
    label: '引用（明朝）',
    description: '読み物用の引用。明朝体・左ボーダー。',
    acceptsChildren: false,
    textAs: 'joined',
    component: ProseQuote,
    params: [{ key: 'text', type: 'string', required: true, description: '引用文（Markdown 可）' }],
    template: [
      '<--- ProseQuote --->',
      'text:',
      ' - 引用文をここに書く。',
      '<--- !ProseQuote --->',
    ].join('\n'),
  },
  {
    name: 'ProseList',
    group: 'prose',
    label: '箇条書き（明朝）',
    description: '読み物用の箇条書き。各項目の先頭に「—」が付く明朝スタイル。',
    acceptsChildren: false,
    textAs: 'array',
    component: ProseList,
    params: [{ key: 'text', type: 'string[]', required: true, description: '各項目（`- 項目`）' }],
    template: ['<--- ProseList --->', 'text:', ' - 項目1', ' - 項目2', '<--- !ProseList --->'].join(
      '\n',
    ),
  },
  {
    name: 'ProseTable',
    group: 'prose',
    label: 'テーブル（明朝）',
    description: '読み物用のテーブル。紙面デザインに合わせた配色。ヘッダー/各行を `|` で区切る。',
    acceptsChildren: false,
    textAs: 'array',
    component: ProseTable,
    params: [
      {
        key: 'headers',
        type: 'string[]',
        required: false,
        description: 'ヘッダー行（`|` 区切り）',
      },
      { key: 'text', type: 'string[]', required: true, description: 'データ行（各行 `|` 区切り）' },
    ],
    template: [
      '<--- ProseTable --->',
      'headers:',
      ' - 技術|外部化した認知機能',
      'text:',
      ' - 文字|記憶',
      ' - 計算機|計算',
      '<--- !ProseTable --->',
    ].join('\n'),
  },
  {
    name: 'ProseSource',
    group: 'prose',
    label: '出典注記（明朝）',
    description:
      '本文直後や章末に添える小さな出典注記。上に細い区切り線を引き、各項目の頭に「―」を付けた淡色の一覧で示す。引用元（書名・巻・章）や参考書籍・人物の出典を、本文の流れを止めずに示すのに使う。',
    acceptsChildren: false,
    textAs: 'array',
    component: ProseSource,
    params: [
      {
        key: 'title',
        type: 'string',
        required: false,
        description: '見出し文字',
        sampleValues: ['出典', '注', '参考'],
        defaultValue: '出典',
      },
      { key: 'text', type: 'string[]', required: true, description: '出典の各項目（`- 項目`）' },
    ],
    template: [
      '<--- ProseSource --->',
      'text:',
      ' - マルクス『資本論』第1巻 第1篇 第1章「商品」',
      '<--- !ProseSource --->',
    ].join('\n'),
    notes: [
      '本文（ProseText / ProseQuote）の直後や章末に置くと、新書風の「章末注」になります。',
      'title を省略すると「出典」と表示されます。「注」「参考」などに変えられます。',
    ],
  },
  {
    name: 'ProseEpigraph',
    group: 'prose',
    label: 'エピグラフ（明朝）',
    description:
      '章タイトルの直後・本文の前に置く題辞（エピグラフ）。短い引用と出典を、字下げ・小さめ・右寄せで本文と区別して示し、章のテーマを一撃で予感させる。',
    acceptsChildren: false,
    textAs: 'joined',
    component: ProseEpigraph,
    params: [
      { key: 'text', type: 'string', required: true, description: '引用文（Markdown 可）' },
      { key: 'cite', type: 'string', required: false, description: '出典' },
    ],
    template: [
      '<--- ProseEpigraph --->',
      'text:',
      ' - 同じ場所に留まるためには、全力で走り続けなければならない。',
      'cite:ルイス・キャロル『鏡の国のアリス』',
      '<--- !ProseEpigraph --->',
    ].join('\n'),
    notes: [
      'ProseQuote でも代用できますが、章の冒頭に置く出典付きの特別な引用にはこちらを使います。',
    ],
  },
  {
    name: 'ProseCallout',
    group: 'prose',
    label: '強調囲み（明朝）',
    description:
      '背骨の一文・一番伝えたい主張を際立たせる強調囲み。本文と同じ明朝のまま、左のアクセントボーダーとわずかな背景で区別する。紙面の静けさを保つため、記事全体で1〜2箇所だけの使用が目安。',
    acceptsChildren: false,
    textAs: 'joined',
    component: ProseCallout,
    params: [
      { key: 'text', type: 'string', required: true, description: '本文（Markdown 可）' },
      {
        key: 'variant',
        type: 'string',
        required: false,
        description: '強調の度合い（strong で太字・濃いボーダー）',
        sampleValues: ['quiet', 'strong'],
        defaultValue: 'quiet',
      },
    ],
    template: [
      '<--- ProseCallout --->',
      'text:',
      ' - 還元を抜きに、本当に誰かが幸せになるのか。',
      '<--- !ProseCallout --->',
    ].join('\n'),
  },
];

/** インライン要素定義 */
export const INLINE_DEFS: InlineDef[] = [
  {
    name: 'kbd',
    label: 'キーボードキー',
    description: 'キーボードのキー表示。',
    valueDescription: 'キー（`+` 区切りで複数キー）',
    sampleValues: ['Ctrl+S', 'Cmd+P'],
    component: KbdInline,
  },
  {
    name: 'badge',
    label: 'バッジ',
    description: 'インラインの小さなラベル。ロール名でロールカラー適用。',
    valueDescription: 'ラベル（`ラベル|色` で色指定可）',
    sampleValues: ['実験中', 'ADMIN', '重要|error.main'],
    component: BadgeInline,
  },
  {
    name: 'mention',
    label: 'ユーザーメンション',
    description: '社内ユーザーをリンク表示。`@userId` でも書ける。',
    valueDescription: 'userId',
    sampleValues: ['yamada'],
    component: UserMentionInline,
  },
];

// --- 索引・照会 ---

const BLOCK_MAP = new Map(COMPONENT_DEFS.map((d) => [d.name, d]));
const INLINE_MAP = new Map(INLINE_DEFS.map((d) => [d.name, d]));

export const getComponentDef = (name: string): ComponentDef | undefined => BLOCK_MAP.get(name);
export const getInlineDef = (name: string): InlineDef | undefined => INLINE_MAP.get(name);

/**
 * コンポーネントをグループごとにまとめて返す（ツールバーのポップアップ・
 * ガイドのツリー表示に使用）。COMPONENT_GROUP_LABELS の順を維持し、
 * 所属コンポーネントが 0 件のグループは含めない。
 */
export const getGroupedComponents = (): { key: string; label: string; defs: ComponentDef[] }[] =>
  COMPONENT_GROUP_LABELS.map((g) => ({
    key: g.key,
    label: g.label,
    defs: COMPONENT_DEFS.filter((d) => d.group === g.key),
  })).filter((g) => g.defs.length > 0);

/** パーサに渡す registry 照会 */
export const registryLookup: RegistryLookup = {
  getTextAs: (name) => BLOCK_MAP.get(name)?.textAs,
  hasBlock: (name) => BLOCK_MAP.has(name),
  hasInline: (name) => INLINE_MAP.has(name),
};
